import { REDUCED_MOTION } from '@utils/Motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const DEFAULTS = {
    duration: 0.6,
    ease: 'expo.out',
    staggerDelay: 0.12,
};

export function initScrollAnimations() {
    if (REDUCED_MOTION) {
        gsap.set('.animate-on-scroll, [data-sequence], [data-scale-in], .section-label', {
            autoAlpha: 1, y: 0, x: 0, scale: 1, clipPath: 'none',
        });
        return;
    }

    initFadeUp();
    initStagger();
    initBannerSequence();
    initAccentLineDraw();
    initSectionLabelReveal();
    initImageScaleIn();
    initSwiperAutoplay();
    initHeroTilt();
    initTestimonialStars();
}

// Mirrors initStagger's `:scope > .animate-on-scroll, :scope > * > .animate-on-scroll`;
// anything nested deeper inside a stagger container still fades up on its own.
function isStaggerChild(el: HTMLElement): boolean {
    const parent = el.parentElement;

    return Boolean(parent?.hasAttribute('data-stagger') || parent?.parentElement?.hasAttribute('data-stagger'));
}

function initFadeUp() {
    gsap.utils.toArray<HTMLElement>('.animate-on-scroll').forEach(el => {
        if (isStaggerChild(el)) return;

        gsap.fromTo(el,
            { y: 24, autoAlpha: 0 },
            {
                y: 0,
                autoAlpha: 1,
                duration: DEFAULTS.duration,
                ease: DEFAULTS.ease,
                scrollTrigger: {
                    trigger: el,
                    start: 'top 90%',
                    once: true,
                },
            },
        );
    });
}

function initStagger() {
    gsap.utils.toArray<HTMLElement>('[data-stagger]').forEach(parent => {
        const children = parent.querySelectorAll<HTMLElement>(':scope > .animate-on-scroll, :scope > * > .animate-on-scroll');
        if (!children.length) return;

        const delay = parseFloat(parent.dataset.stagger || '') || DEFAULTS.staggerDelay;

        gsap.fromTo(children,
            { y: 30, autoAlpha: 0 },
            {
                y: 0,
                autoAlpha: 1,
                duration: DEFAULTS.duration,
                ease: DEFAULTS.ease,
                stagger: delay,
                scrollTrigger: {
                    trigger: parent,
                    start: 'top 85%',
                    once: true,
                },
            },
        );
    });
}

function initBannerSequence() {
    gsap.utils.toArray<HTMLElement>('[data-banner-sequence]').forEach(banner => {
        const items = banner.querySelectorAll<HTMLElement>('[data-sequence]');
        if (!items.length) return;

        const sorted = Array.from(items).sort((a, b) =>
            (parseInt(a.dataset.sequence!) || 0) - (parseInt(b.dataset.sequence!) || 0),
        );

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: banner,
                start: 'top 80%',
                once: true,
            },
        });

        sorted.forEach((el, i) => {
            const isImage = el.hasAttribute('data-sequence-image');

            if (isImage) {
                tl.fromTo(el,
                    { x: 30, autoAlpha: 0, clipPath: 'inset(0 100% 0 0 round 24px)' },
                    { x: 0, autoAlpha: 1, clipPath: 'inset(0 0% 0 0 round 24px)', duration: 0.9, ease: 'expo.out' },
                    i * 0.12,
                );
            } else {
                tl.fromTo(el,
                    { y: 20, autoAlpha: 0 },
                    { y: 0, autoAlpha: 1, duration: 0.6, ease: 'expo.out' },
                    i * 0.12,
                );
            }
        });
    });
}

function initAccentLineDraw() {
    gsap.utils.toArray<HTMLElement>('[data-line-draw]').forEach(el => {
        gsap.fromTo(el,
            { scaleX: 0 },
            {
                scaleX: 1,
                transformOrigin: el.dataset.lineDraw === 'center' ? 'center center' : 'left center',
                duration: 0.6,
                ease: 'expo.out',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 90%',
                    once: true,
                },
            },
        );
    });
}

function initSectionLabelReveal() {
    gsap.utils.toArray<HTMLElement>('.section-label').forEach(el => {
        gsap.fromTo(el,
            { scaleX: 0, autoAlpha: 0, transformOrigin: 'left center' },
            {
                scaleX: 1,
                autoAlpha: 1,
                duration: 0.5,
                ease: 'expo.out',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 90%',
                    once: true,
                },
            },
        );
    });
}

function initImageScaleIn() {
    gsap.utils.toArray<HTMLElement>('[data-scale-in]').forEach(el => {
        gsap.fromTo(el,
            { scale: 1.04, autoAlpha: 0 },
            {
                scale: 1,
                autoAlpha: 1,
                duration: 0.9,
                ease: 'expo.out',
                scrollTrigger: {
                    trigger: el,
                    start: 'top 85%',
                    once: true,
                },
            },
        );
    });
}

function initHeroTilt() {
    const hero = document.querySelector<HTMLElement>('[data-banner-sequence]');
    const imageWrap = hero?.querySelector<HTMLElement>('[data-sequence-image]');
    if (!hero || !imageWrap || window.innerWidth < 1024) return;

    const parent = imageWrap.parentElement;
    if (parent) parent.style.perspective = '800px';

    const MAX_ROTATE = 2.5;

    hero.addEventListener('mousemove', (e: MouseEvent) => {
        const rect = hero.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;

        gsap.to(imageWrap, {
            rotateY: x * MAX_ROTATE * 2,
            rotateX: -y * MAX_ROTATE,
            duration: 0.6,
            ease: 'expo.out',
            overwrite: 'auto',
        });
    }, { passive: true });

    hero.addEventListener('mouseleave', () => {
        gsap.to(imageWrap, {
            rotateY: 0,
            rotateX: 0,
            duration: 0.8,
            ease: 'expo.out',
            overwrite: 'auto',
        });
    }, { passive: true });
}

function initTestimonialStars() {
    gsap.utils.toArray<HTMLElement>('.testimonial-stars').forEach(container => {
        const stars = container.querySelectorAll<HTMLElement>('span');
        if (!stars.length) return;

        gsap.set(stars, { autoAlpha: 0, scale: 0.5 });

        ScrollTrigger.create({
            trigger: container.closest('.testimonial-card') || container,
            start: 'top 85%',
            once: true,
            onEnter: () => {
                gsap.to(stars, {
                    autoAlpha: 1,
                    scale: 1,
                    duration: 0.3,
                    ease: 'expo.out',
                    stagger: 0.06,
                });
            },
        });
    });
}

function initSwiperAutoplay() {
    requestAnimationFrame(() => {
        document.querySelectorAll<HTMLElement>('[data-autoplay-on-scroll]').forEach(el => {
            const swiperInstance = (el as any).swiper;
            if (!swiperInstance?.autoplay) return;

            swiperInstance.autoplay.stop();

            ScrollTrigger.create({
                trigger: el,
                start: 'top 70%',
                once: true,
                onEnter: () => swiperInstance.autoplay.start(),
            });
        });
    });
}
